import { useState } from 'react';
import { Loader2, LineChart, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-lg border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-xl font-bold tabular-nums">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function fmt(v) {
  if (v == null) return '--';
  const n = Number(v);
  return isNaN(n) ? v : n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function VerdictBanner({ verdict }) {
  if (!verdict) return null;

  const isGreen = verdict.includes('No bug');
  const isRed = verdict.includes('Confirmed bug');
  // amber for everything else (partial filtering, etc.)

  let icon, bg;
  if (isRed) {
    icon = <XCircle className="h-5 w-5 text-red-500 shrink-0" />;
    bg = 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800';
  } else if (isGreen) {
    icon = <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />;
    bg = 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800';
  } else {
    icon = <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />;
    bg = 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800';
  }

  return (
    <div className={`flex items-start gap-3 rounded-lg border p-4 ${bg}`}>
      {icon}
      <p className="text-sm font-medium">{verdict}</p>
    </div>
  );
}

export function NightlyRateCancelledExclusionPanel() {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const runCheck = async () => {
    setLoading(true);
    setResults(null);
    try {
      const { data } = await diagnosticsAPI.nightlyRateCancelledExclusion();
      setResults(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <LineChart className="h-5 w-5 text-blue-500" />
          Nightly Rate Cancelled Exclusion
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Computes the portfolio nightly rate three ways (as-chart, excluding-cancelled,
          including-cancelled) to determine whether the rate aggregation still counts
          cancelled bookings after the backfill zeroed their revenue.
        </p>

        <Button onClick={runCheck} disabled={loading}>
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <LineChart className="h-4 w-4 mr-2" />
          )}
          Run Check
        </Button>

        {results && (
          <div className="space-y-5">
            <VerdictBanner verdict={results.verdict} />

            {/* Three rates side by side */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Stat
                label="Rate as chart computes"
                value={`\u00A3${fmt(results.rate_as_chart_computes)}`}
              />
              <Stat
                label="Rate excluding cancelled"
                value={`\u00A3${fmt(results.rate_excluding_cancelled)}`}
                sub={`Delta: \u00A3${fmt(results.delta_chart_vs_excluding)}`}
              />
              <Stat
                label="Rate including cancelled"
                value={`\u00A3${fmt(results.rate_including_cancelled)}`}
                sub={`Delta: \u00A3${fmt(results.delta_chart_vs_including)}`}
              />
            </div>

            {/* Detail stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat
                label="Nights (total incl. cancelled)"
                value={results.nights_total ?? '--'}
              />
              <Stat
                label="Nights (cancelled)"
                value={results.nights_cancelled ?? '--'}
              />
              <Stat
                label="Cancelled non-zero rate?"
                value={results.cancelled_contributing_nonzero_rate ? 'YES' : 'NO'}
                sub={results.cancelled_contributing_nonzero_rate
                  ? 'Cancelled bookings have non-zero revenue in the rate field'
                  : 'All cancelled bookings have zeroed revenue'}
              />
              <Stat
                label="Rate field read"
                value={results.rate_field_read ?? '--'}
              />
            </div>

            {/* Window and FX info */}
            <div className="text-xs text-muted-foreground space-y-1">
              {results.window && (
                <p>Window: {results.window.from} to {results.window.to}</p>
              )}
              {results.fx_rates_used?.EUR_GBP && (
                <p>EUR/GBP rate used: {results.fx_rates_used.EUR_GBP}</p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
